import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  IOrderMenuCategory,
  IOrderMenuCategorySection,
  IOrderMenuItem,
  IOrderMenuItemGallery,
  TopLevelMenuEntityList
} from '../pages/v2-order-pay/models/order-menu.models';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { LocationStore } from 'stores/location.store';
import { OrderMenuItemPinningStore } from 'stores/order-menu-item-pinning.store';
import * as rfdc from 'rfdc';

@Injectable({
  providedIn: 'root'
})
export class OrderMenuStore {
  constructor(
    private locationStore: LocationStore,
    private orderMenuItemPinningStore: OrderMenuItemPinningStore
  ) {}

  private readonly _itemGalleries$ = new BehaviorSubject<
    IOrderMenuItemGallery[]
  >([]);

  readonly itemGalleries$: Observable<IOrderMenuItemGallery[]> = combineLatest([
    this._itemGalleries$,
    this.locationStore.isAlcoholOrderingEnabled$,
    this.locationStore.isMenuReadonly$,
    this.orderMenuItemPinningStore.pinnedMenuItemIds$,
    this.orderMenuItemPinningStore.isPinningEnabled$
  ]).pipe(
    map(
      ([
        itemGalleries,
        isAlcoholOrderingEnabled,
        isMenuReadonly,
        pinnedMenuItemIds,
        isPinningEnabled
      ]) =>
        itemGalleries
          // Remove items from galleries that have no image
          .map((gallery) => ({
            ...gallery,
            items: this.mapItemsWithAdditionalInfo(
              gallery.items,
              isAlcoholOrderingEnabled,
              isMenuReadonly,
              pinnedMenuItemIds,
              isPinningEnabled
            )
          }))
          // Remove galleries with no items
          .filter((gallery) => gallery.items.length > 0)
    )
  );

  set itemGalleries(v: IOrderMenuItemGallery[]) {
    this._itemGalleries$.next(v);
  }

  private readonly _orderMenuCategories$: BehaviorSubject<
    IOrderMenuCategory[]
  > = new BehaviorSubject<IOrderMenuCategory[]>([]);

  get orderMenuCategories(): IOrderMenuCategory[] {
    return this._orderMenuCategories$.getValue();
  }

  set orderMenuCategories(v: IOrderMenuCategory[]) {
    this._orderMenuCategories$.next(v);
  }

  readonly orderMenuCategories$: Observable<IOrderMenuCategory[]> =
    combineLatest([
      this._orderMenuCategories$,
      this.itemGalleries$,
      this.locationStore.isAlcoholOrderingEnabled$,
      this.locationStore.isMenuReadonly$,
      this.orderMenuItemPinningStore.pinnedMenuItemIds$,
      this.orderMenuItemPinningStore.isPinningEnabled$
    ]).pipe(
      map(
        ([
          categories,
          itemGalleries,
          isAlcoholOrderingEnabled,
          isMenuReadonly,
          pinnedMenuItemIds,
          isPinningEnabled
        ]) => {
          categories = categories.map((category) => ({
            ...category,
            nestedItemGalleries: category.nestedItemGalleries.map(
              (itemGallery) => ({
                ...itemGallery,
                items: this.mapItemsWithAdditionalInfo(
                  itemGallery.items,
                  isAlcoholOrderingEnabled,
                  isMenuReadonly,
                  pinnedMenuItemIds,
                  isPinningEnabled
                )
              })
            ),
            sections: category.sections.map((section) => ({
              ...section,
              nestedItemGalleries: section.nestedItemGalleries.map(
                (itemGallery) => ({
                  ...itemGallery,
                  items: this.mapItemsWithAdditionalInfo(
                    itemGallery.items,
                    isAlcoholOrderingEnabled,
                    isMenuReadonly,
                    pinnedMenuItemIds,
                    isPinningEnabled
                  )
                })
              ),
              items: this.mapItemsWithAdditionalInfo(
                section.items,
                isAlcoholOrderingEnabled,
                isMenuReadonly,
                pinnedMenuItemIds,
                isPinningEnabled
              )
            }))
          }));

          const combinedEntityList = this.buildCombinedMenuEntityList(
            categories,
            itemGalleries
          );

          return this.nestItemGalleriesWithinCategories(combinedEntityList);
        }
      )
    );

  private mapItemsWithAdditionalInfo(
    items: IOrderMenuItem[],
    isAlcoholOrderingEnabled: boolean,
    isMenuReadonly: boolean,
    pinnedItemIds: string[],
    isPinningEnabled: boolean
  ): IOrderMenuItem[] {
    return items.map((item) => {
      const isUnavailableDueToAlcohol = this.isItemUnavailableDueToAlcohol(
        item,
        isAlcoholOrderingEnabled
      );
      return {
        ...item,
        isUnavailableDueToAlcohol: isUnavailableDueToAlcohol,
        isDisabled: this.isItemDisabled(
          item.inStock,
          isUnavailableDueToAlcohol,
          isMenuReadonly
        ),
        isPinned: isPinningEnabled && pinnedItemIds.includes(item.id),
        items: this.mapItemsWithAdditionalInfo(
          item.items,
          isAlcoholOrderingEnabled,
          isMenuReadonly,
          pinnedItemIds,
          isPinningEnabled
        )
      };
    });
  }

  private buildCombinedMenuEntityList(
    categories: IOrderMenuCategory[],
    itemGalleries: IOrderMenuItemGallery[]
  ): TopLevelMenuEntityList {
    const accumulator: TopLevelMenuEntityList = [];
    categories.forEach(
      (category) => (accumulator[category.sortOrder] = category)
    );
    itemGalleries.forEach(
      (itemGallery) => (accumulator[itemGallery.sortOrder] = itemGallery)
    );
    return accumulator.filter(Boolean);
  }

  // Make the order of item galleries relative to categories by nesting them
  private nestItemGalleriesWithinCategories(
    topLevelEntities: TopLevelMenuEntityList
  ): IOrderMenuCategory[] {
    let pendingGalleries: IOrderMenuItemGallery[] = [];
    const categoriesWithNestedGalleries: IOrderMenuCategory[] = [];
    topLevelEntities.forEach((originalEntity) => {
      const entity: IOrderMenuCategory | IOrderMenuItemGallery =
        rfdc()(originalEntity);

      if (entity.entityType === 'item-gallery') {
        const lastCategory =
          categoriesWithNestedGalleries[
            categoriesWithNestedGalleries.length - 1
          ];
        if (lastCategory) {
          lastCategory.itemGalleriesAfter.push(entity);
        } else {
          pendingGalleries.push(entity);
        }
      } else if (entity.entityType === 'category') {
        if (pendingGalleries.length > 0) {
          entity.itemGalleriesBefore = [...pendingGalleries];
          pendingGalleries = [];
        }
        categoriesWithNestedGalleries.push(entity);
      }
    });

    return categoriesWithNestedGalleries;
  }

  private readonly expandedCategoryHeaderIds$: BehaviorSubject<Set<string>> =
    new BehaviorSubject<Set<string>>(new Set<string>());

  private readonly expandedSectionIds$: BehaviorSubject<Set<string>> =
    new BehaviorSubject<Set<string>>(new Set<string>());

  private readonly _highlightedCategoryId$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  readonly highlightedCategoryId$: Observable<string> =
    this._highlightedCategoryId$.pipe(distinctUntilChanged());

  highlightCategory(categoryId: string) {
    this._highlightedCategoryId$.next(categoryId);
  }

  private readonly _highlightedSectionId$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  readonly highlightedSectionId$: Observable<string> =
    this._highlightedSectionId$.pipe(distinctUntilChanged());

  highlightSection(sectionId: string) {
    this._highlightedSectionId$.next(sectionId);
  }

  private readonly _scrollToCategoryId$: Subject<string> =
    new Subject<string>();
  readonly scrollToCategoryId$: Observable<string> =
    this._scrollToCategoryId$.asObservable();

  scrollToCategory(categoryId: string) {
    this._scrollToCategoryId$.next(categoryId);
  }

  private readonly _scrollToSectionId$: Subject<string> = new Subject<string>();
  readonly scrollToSectionId$: Observable<string> =
    this._scrollToSectionId$.asObservable();

  scrollToSection(sectionId: string) {
    this._scrollToSectionId$.next(sectionId);
  }

  readonly orderMenuCategoriesWithExpansionData$: Observable<
    IOrderMenuCategory[]
  > = combineLatest([
    this.orderMenuCategories$,
    this.expandedSectionIds$,
    this.expandedCategoryHeaderIds$
  ]).pipe(
    map(([orderMenuCategories, expandedSectionIds, expandedCategoryHeader]) =>
      orderMenuCategories
        .map((category) => ({
          ...category,
          sections: category.sections.map((section) => ({
            ...section,
            isExpanded: expandedSectionIds.has(section.id)
          }))
        }))
        .map((category) => ({
          ...category,
          // if category has description, base expanded state on the expanded category header state
          // otherwise, base expanded state on the category's sections expanded state
          isExpanded: category.description
            ? expandedCategoryHeader.has(category.id)
            : category.sections.every((section) => section.isExpanded)
        }))
    )
  );

  toggleExpandSection(section: IOrderMenuCategorySection) {
    const expandedSectionIds = this.expandedSectionIds$.getValue();
    this.expandedSectionIds$.next(
      new Set(
        expandedSectionIds.has(section.id)
          ? [...expandedSectionIds].filter((id) => id !== section.id)
          : [...expandedSectionIds].concat(section.id)
      )
    );
  }

  toggleExpandCategory(category: IOrderMenuCategory) {
    const expandedCategoryHeaderIds =
      this.expandedCategoryHeaderIds$.getValue();
    const categorySectionIds = category.sections.map((section) => section.id);
    if (category.isExpanded) {
      this.expandedCategoryHeaderIds$.next(
        new Set(
          expandedCategoryHeaderIds.has(category.id)
            ? [...expandedCategoryHeaderIds].filter((id) => id !== category.id)
            : [...expandedCategoryHeaderIds].concat(category.id)
        )
      );

      this.expandedSectionIds$.next(
        new Set(
          [...this.expandedSectionIds$.getValue()].filter(
            (sectionId) => !categorySectionIds.includes(sectionId)
          )
        )
      );
    } else {
      this.expandedCategoryHeaderIds$.next(
        new Set([...expandedCategoryHeaderIds, category.id])
      );

      this.expandedSectionIds$.next(
        new Set([...this.expandedSectionIds$.getValue(), ...categorySectionIds])
      );
    }
  }

  private readonly _selectedItemGroup$ =
    new BehaviorSubject<IOrderMenuItem | null>(null);
  readonly selectedItemGroup$ = this._selectedItemGroup$.asObservable();

  // TODO make id based not copy based
  set selectedItemGroup(v: IOrderMenuItem) {
    this._selectedItemGroup$.next(v);
  }

  // the entire list of items in the menu and item galleries, with item groups unnested
  readonly orderMenuItems$: Observable<IOrderMenuItem[]> = combineLatest([
    this.orderMenuCategories$,
    this.itemGalleries$
  ]).pipe(
    map(([categories, locationLevelItemGalleries]) =>
      this.extractAllItems(categories, locationLevelItemGalleries)
    )
  );

  private extractAllItems(
    categories: IOrderMenuCategory[],
    locationLevelItemGalleries: IOrderMenuItemGallery[]
  ) {
    const extractItems = (item: IOrderMenuItem) =>
      item.type === 'item' ? [item] : item.items;

    if (categories.length === 0) {
      return locationLevelItemGalleries
        .map((ig) => ig.items.map(extractItems))
        .flat(2);
    }

    const allItems = categories.flatMap((category) => {
      const sectionItems = category.sections.flatMap((section) => [
        ...section.items.flatMap(extractItems),
        ...section.nestedItemGalleries.flatMap((g) =>
          g.items.flatMap(extractItems)
        )
      ]);

      const itemGalleryItems = [
        ...category.nestedItemGalleries,
        ...category.itemGalleriesAfter,
        ...category.itemGalleriesBefore
      ].flatMap((ig) => ig.items.flatMap(extractItems));

      return [...sectionItems, ...itemGalleryItems];
    });

    return allItems;
  }

  private isItemUnavailableDueToAlcohol(
    item: IOrderMenuItem,
    isAlcoholOrderingEnabled: boolean | null
  ): boolean {
    let alcohol = item.attributes.alcohol;
    if (this.isItemGroupWithMultipleItems(item)) {
      alcohol = item.items.every((groupItem) =>
        this.isItemUnavailableDueToAlcohol(groupItem, isAlcoholOrderingEnabled)
      );
    }

    return !isAlcoholOrderingEnabled && alcohol;
  }

  private isItemDisabled(
    inStock: boolean,
    isUnavailableDueToAlcohol: boolean,
    isMenuReadonly: boolean
  ): boolean {
    return !isMenuReadonly && (isUnavailableDueToAlcohol || !inStock);
  }

  isItemGroupWithMultipleItems(item: IOrderMenuItem): boolean {
    return item.type === 'itemGroup' && item.items.length > 1;
  }

  private readonly _viewScrollingToElementCounter$ = new BehaviorSubject(0);

  readonly isViewAutoScrollingToElement$: Observable<boolean> =
    this._viewScrollingToElementCounter$.pipe(
      map(
        (viewScrollingToElementCounter) => viewScrollingToElementCounter !== 0
      )
    );

  viewBeganAutoScrollingToElement() {
    this._viewScrollingToElementCounter$.next(
      this._viewScrollingToElementCounter$.getValue() + 1
    );
  }

  viewFinishedAutoScrollingToElement() {
    this._viewScrollingToElementCounter$.next(
      this._viewScrollingToElementCounter$.getValue() - 1
    );
  }

  readonly pinnedMenuItems$: Observable<IOrderMenuItem[]> = combineLatest([
    this.orderMenuItemPinningStore.pinnedMenuItemIds$,
    this.orderMenuItems$
  ]).pipe(
    map(
      ([pinnedMenuItemIds, orderMenuItems]) =>
        pinnedMenuItemIds
          .map((itemId) => orderMenuItems.find((item) => item.id === itemId))
          .filter(Boolean) as IOrderMenuItem[]
    )
  );
}
